import { BaseListViewStore, BooleanToggleCellData, CloseOutsCellData } from 'core/stores';
import { action, runInAction } from 'mobx';
import { ConfirmModalParams, ModalParams } from 'core/models';
import { observable, makeObservable, reaction } from 'mobx';
import { notificationError, isSuperAdminOrganization } from 'core/utils';
import { WarehouseOrganizationInfoStore } from './';
import { sourceOfChange } from 'common/constants';
import { ApplicationStore } from 'platform/application/stores';
import _ from 'lodash';

class WarehouseListViewStore extends BaseListViewStore {
	@observable applicationSort = [{ field: 'applicationTitle', dir: 'asc' }];
	application;

	constructor(rootStore) {
		super(rootStore, {
			listStore: new ApplicationStore(rootStore),
			queryParams: {
				embed: ['warehouseOrganizationInfo', 'closeOuts'],
			},
			findFunction: 'findWarehouse',
			tableData: {
				columns: [
					{
						key: 'applicationTitle',
						title: 'WAREHOUSELISTVIEW.TITLE',
						sortable: true,
						onClick: warehouse => this.routes.edit(warehouse.id),
						selected: true,
					},
					{
						key: 'identifier',
						title: 'WAREHOUSELISTVIEW.IDENTIFIER',
						sortable: true,
					},
					{
						key: 'warehouseOrganizationInfo.isAvailable',
						title: 'WAREHOUSELISTVIEW.AVAILABLE',
						sortable: true,
						selected: true,
						dataCell: BooleanToggleCellData,
						onClick: (warehouse, field) => this.onToggle(warehouse, field, sourceOfChange.isAvailableToggleWarehouseList),
						isClickable: true,
					},
					{
						key: 'warehouseOrganizationInfo.useAutoExitScan',
						title: 'WAREHOUSELISTVIEW.AUTOEXITSCAN',
						sortable: true,
						selected: true,
						dataCell: BooleanToggleCellData,
						onClick: (warehouse, field) => this.onToggle(warehouse, field, sourceOfChange.useAutoscanWarehouseList),
						isClickable: true,
					},
					{
						key: 'warehouseOrganizationInfo.acceptingReturns',
						title: 'WAREHOUSELISTVIEW.ACCEPTINGRETURNS',
						sortable: true,
						selected: true,
						dataCell: BooleanToggleCellData,
						onClick: (warehouse, field) => this.onToggle(warehouse, field, sourceOfChange.acceptingReturns),
						isClickable: true,
					},
					{
						key: 'closeOuts[0].palletizingCloseOut',
						title: 'CLOSEOUTPALLETIZING',
						selected: true,
						dataCell: CloseOutsCellData,
						isClickable: false,
						nullAsFalse: true,
					},
					{
						key: 'closeOuts[0].exitScanCloseOut',
						title: 'CLOSEOUTEXITSCAN',
						selected: true,
						dataCell: CloseOutsCellData,
						isClickable: false,
						nullAsFalse: true,
					},
					{
						key: 'warehouseOrganizationInfo.sendTransferAPI',
						title: 'WAREHOUSELISTVIEW.SENDTRANSFERAPI',
						sortable: true,
						selected: true,
						dataCell: BooleanToggleCellData,
						onClick: (warehouse, field) => this.onToggle(warehouse, field, sourceOfChange.sendTransferAPIWarehouseList),
						isClickable: true,
					},
					{
						key: 'warehouseOrganizationInfo.sendOrderAPI',
						title: 'WAREHOUSELISTVIEW.SENDORDERAPI',
						sortable: true,
						selected: true,
						dataCell: BooleanToggleCellData,
						onClick: (warehouse, field) => this.onToggle(warehouse, field, sourceOfChange.sendOrderAPIWarehouseList),
						isClickable: true,
					},
					{
						key: 'warehouseOrganizationInfo.sendInventoryAPI',
						title: 'WAREHOUSELISTVIEW.SENDINVENTORYAPI',
						sortable: true,
						selected: true,
						dataCell: BooleanToggleCellData,
						onClick: (warehouse, field) => this.onToggle(warehouse, field, sourceOfChange.sendInventoryAPIWarehouseList),
						isClickable: true,
					},
				],
				sortObject: [
					{
						field: 'applicationTitle',
						dir: 'asc',
					},
				],
				actions: {
					onEdit: application => this.routes.edit(application.id),
					onChangeOrganizationActive: item => this.openConfirmationModal(this.getActivationConfirmModalProps(item)),
					onDelete: application => this.openConfirmationModal(this.getDeleteConfirmModalProps(application)),
				},
			},
			routes: {
				create: () => this.rootStore.routerStore.goTo('master.app.main.warehouse.create'),
				edit: id =>
					this.rootStore.routerStore.goTo('master.app.main.warehouse.edit', {
						id,
					}),
			},
			permissions: {
				onEdit: () => this.authStore.hasPermission(),
				onChangeOrganizationActive: () => this.authStore.hasPermission(),
				onDelete: () => this.authStore.hasPermission(),
			},
		});
		makeObservable(this);
		if (isSuperAdminOrganization(this.rootStore)) {
			this.rootStore.viewStore.handleSellerForChildViewStore(this);
		}
		this.queryUtility.initialize(this.findWarehouse);
		reaction(
			() => this.rootStore.routerStore.routerState,
			state => {
				if (state.routeName !== 'master.app.main.warehouse.list') {
					return;
				}

				const { editId, create } = state.queryParams;

				this.editModal.setOpen(editId !== undefined || create !== undefined);
			}
		);
		this.warehouseOrganizationInfoStore = new WarehouseOrganizationInfoStore(rootStore);
		this.setButtons();
		this.setSearchVisible();
	}

	@action.bound
	async onToggle(warehouse, field, sourceOfChange) {
		this.suspend();
		try {
			var splitField = field.split('.');
			const warehouseOrganizationInfo = _.get(warehouse, splitField[0]);
			warehouseOrganizationInfo[splitField[1]] = !warehouseOrganizationInfo[splitField[1]];
			await this.warehouseOrganizationInfoStore.updateWarehouseOrganizationInfo(
				warehouseOrganizationInfo,
				sourceOfChange
			);
			this.rootStore.notificationStore.success([
				'LOOKUPUPDATE.WAREHOUSEORGANIZATIONINFO',
				{ name: warehouse.applicationTitle },
			]);
		} catch (err) {
			notificationError({ store: this.rootStore.notificationStore, err });
		}
		this.resume();
	}

	@action.bound async findWarehouse() {
		this.suspend();
		runInAction(() => {
			//Default sorting
			this.queryUtility.filter.warehouseOnly = true;
			this.queryUtility.filter.getOnlyTodayCloseOut = true;
		});
		await this.listStore.find(this.queryUtility.filter);
		if (this.listStore.response.item.length === 0 && this.listStore.response.page !== 1) {
			await this.queryUtility.changePage(1);
		}
		this.queryUtility.handleResponse(this.listStore.response);
		this.resume();
	}

	@action.bound setButtons() {
		this.setButtonComponent({
			buttons: [
				{
					className: 'btn btn--dropdown header__button',
					iconClassName: 'fa fa-plus spc--right--sml',
					onClick: this.routes.create,
					text: 'WAREHOUSEVIEW.NEW',
				},
			],
		});
	}

	@action.bound closeEditModal() {
		this.rootStore.routerStore.updateQueryParams({
			editId: undefined,
			create: undefined,
			...this.queryUtility.getFilterUrlParams(),
		});
	}

	editModal = new ModalParams({
		onClose: this.closeEditModal,
	});

	getDeleteConfirmModalProps = item => {
		return {
			onConfirm: async () => await this.deleteOrganization(this.rootStore.viewStore.selectedConfirmModal.data),
			message: ['LOOKUPDELETECONFIRM.ORGANIZATION', { name: item.applicationTitle }],
			data: item,
		};
	};

	@action.bound async changeOrganizationActive(application) {
		this.suspend();
		try {
			await this.listStore.changeStatus(application.id, !application.isActive);
			this.rootStore.notificationStore.success([
				!application.isActive ? 'ORGANIZATION.LIST.REACTIVATESUCCESS' : 'ORGANIZATION.LIST.DEACTIVATESUCCESS',
				{ name: application.identifier },
			]);
			await this.queryUtility.initialize(false);
		} catch (err) {
			notificationError({ store: this.rootStore.notificationStore, err });
		}
		this.resume();
	}

	@action.bound
	deleteOrganization = async organization => {
		this.suspend();
		try {
			await this.listStore.deleteApplication(organization);
			this.rootStore.notificationStore.success(['LOOKUPDELETE.ORGANIZATION', { name: organization.applicationTitle }]);
			await this.queryUtility.initialize(false);
		} catch (err) {
			notificationError({ store: this.rootStore.notificationStore, err });
		}
		this.resume();
	};

	getActivationConfirmModalProps = item => {
		return {
			onConfirm: async () => await this.changeOrganizationActive(this.rootStore.viewStore.selectedConfirmModal.data),
			message: [
				!item.isActive ? 'ORGANIZATION.LIST.REACTIVATECONFIRM' : 'ORGANIZATION.LIST.DEACTIVATECONFIRM',
				{ name: item.identifier },
			],
			data: item,
		};
	};
}

export default WarehouseListViewStore;



// WEBPACK FOOTER //
// ./src/modules/warehouse/stores/WarehouseListViewStore.js